import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Alert,
  Modal,
  ScrollView,
  ActivityIndicator,
  Switch,
  TextInput,
  Dimensions,
  Animated,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
} from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useAI, AIChatWidget } from '../../../services/AIBrainService';

const { width, height } = Dimensions.get('window');

// WebSocket connection manager
class WebSocketManager {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectInterval = 1000;
    this.listeners = new Map();
    this.isConnected = false;
  }

  connect(url, onConnect, onDisconnect, onError) {
    try {
      this.ws = new WebSocket(url);
      
      this.ws.onopen = () => {
        console.log('WebSocket connected');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        if (onConnect) onConnect();
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) {
          console.error('WebSocket message parse error:', error);
        }
      };

      this.ws.onclose = () => {
        console.log('WebSocket disconnected');
        this.isConnected = false;
        if (onDisconnect) onDisconnect();
        this.attemptReconnect(url, onConnect, onDisconnect, onError);
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        if (onError) onError(error);
      };
    } catch (error) {
      console.error('WebSocket connection error:', error);
      if (onError) onError(error);
    }
  }

  attemptReconnect(url, onConnect, onDisconnect, onError) {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      setTimeout(() => {
        console.log(`Attempting to reconnect... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
        this.connect(url, onConnect, onDisconnect, onError);
      }, this.reconnectInterval * this.reconnectAttempts);
    }
  }

  send(data) {
    if (this.ws && this.isConnected) {
      this.ws.send(JSON.stringify(data));
    } else {
      console.warn('WebSocket not connected, message not sent:', data);
    }
  }

  addListener(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  removeListener(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  handleMessage(data) {
    const { event, payload } = data;
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => callback(payload));
    }
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
      this.isConnected = false;
    }
  }
}

// API Manager for REST operations
class APIManager {
  constructor(baseURL) {
    this.baseURL = baseURL;
    this.authToken = null;
  }

  setAuthToken(token) {
    this.authToken = token;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.authToken) {
      headers.Authorization = `Bearer ${this.authToken}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API request error:', error);
      throw error;
    }
  }

  // Website operations
  async getWebsites() {
    return this.request('/api/websites');
  }

  async createWebsite(websiteData) {
    return this.request('/api/websites', {
      method: 'POST',
      body: JSON.stringify(websiteData),
    });
  }

  async updateWebsite(websiteId, websiteData) {
    return this.request(`/api/websites/${websiteId}`, {
      method: 'PUT',
      body: JSON.stringify(websiteData),
    });
  }

  async deleteWebsite(websiteId) {
    return this.request(`/api/websites/${websiteId}`, {
      method: 'DELETE',
    });
  }

  async publishWebsite(websiteId) {
    return this.request(`/api/websites/${websiteId}/publish`, {
      method: 'POST',
    });
  }

  // Content operations
  async getContent(websiteId, contentType) {
    return this.request(`/api/websites/${websiteId}/content/${contentType}`);
  }

  async createContent(websiteId, contentType, contentData) {
    return this.request(`/api/websites/${websiteId}/content/${contentType}`, {
      method: 'POST',
      body: JSON.stringify(contentData),
    });
  }

  async updateContent(websiteId, contentType, contentId, contentData) {
    return this.request(`/api/websites/${websiteId}/content/${contentType}/${contentId}`, {
      method: 'PUT',
      body: JSON.stringify(contentData),
    });
  }

  async deleteContent(websiteId, contentType, contentId) {
    return this.request(`/api/websites/${websiteId}/content/${contentType}/${contentId}`, {
      method: 'DELETE',
    });
  }

  // Analytics operations
  async getAnalytics(websiteId, timeRange = '7d') {
    return this.request(`/api/websites/${websiteId}/analytics?range=${timeRange}`);
  }

  // Sync operations
  async syncToWebsite(websiteId, data) {
    return this.request(`/api/websites/${websiteId}/sync`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async syncFromWebsite(websiteId) {
    return this.request(`/api/websites/${websiteId}/sync`);
  }
}

// Sync Status Component
export const SyncStatus = ({ isConnected, lastSync, onReconnect }) => {
  const [pulseAnim] = useState(new Animated.Value(1));

  const { ask: askAI, agents: aiAgents, loading: aiLoading } = useAI('WebsiteSyncSystem');
  const [showAIChat, setShowAIChat] = useState(false);


  useEffect(() => {
    if (isConnected) {
      Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, {
            toValue: 1.2,
            duration: 1000,
            useNativeDriver: true,
          }),
          Animated.timing(pulseAnim, {
            toValue: 1,
            duration: 1000,
            useNativeDriver: true,
          }),
        ])
      ).start();
    } else {
      pulseAnim.setValue(1);
    }
  }, [isConnected, pulseAnim]);

  return (
    <View style={styles.syncStatusContainer}>
      <View style={styles.syncStatusHeader}>
        <Animated.View style={[
          styles.syncStatusIndicator,
          {
            backgroundColor: isConnected ? '#10B981' : '#EF4444',
            transform: [{ scale: isConnected ? pulseAnim : 1 }]
          }
        ]} />
        <Text style={styles.syncStatusText}>
          <Text>{isConnected ? 'Connected' : 'Disconnected'}</Text>
        </Text>
        {!isConnected && (
          <TouchableOpacity style={styles.reconnectButton} onPress={onReconnect}>
            <Ionicons name="refresh" size={16} color="#667eea" />
          </TouchableOpacity>
        )}
      </View>
      {lastSync && (
        <Text style={styles.lastSyncText}>
          Last sync: {new Date(lastSync).toLocaleString()}
        </Text>
      )}
    </View>
  );
};

// Sync Settings Component
export const SyncSettings = ({ settings, onSettingsChange }) => {
  return (
    <View style={styles.syncSettingsContainer}>
      <Text style={styles.syncSettingsTitle}>Sync Settings</Text>
      
      <View style={styles.syncSetting}>
        <Text style={styles.syncSettingLabel}>Auto Sync</Text>
        <Switch
          value={settings.autoSync}
          onValueChange={(value) => onSettingsChange({ ...settings, autoSync: value })}
          trackColor={{ false: '#E5E5EA', true: '#667eea' }}
          thumbColor="#FFFFFF"
        />
      </View>

      <View style={styles.syncSetting}>
        <Text style={styles.syncSettingLabel}>Real-time Updates</Text>
        <Switch
          value={settings.realTimeUpdates}
          onValueChange={(value) => onSettingsChange({ ...settings, realTimeUpdates: value })}
          trackColor={{ false: '#E5E5EA', true: '#667eea' }}
          thumbColor="#FFFFFF"
        />
      </View>

      <View style={styles.syncSetting}>
        <Text style={styles.syncSettingLabel}>Conflict Resolution</Text>
        <TouchableOpacity style={styles.syncSettingSelect}>
          <Text style={styles.syncSettingSelectText}>
            {settings.conflictResolution === 'app' ? 'App Wins' : 
             settings.conflictResolution === 'website' ? 'Website Wins' : 'Manual'}
          </Text>
          <Ionicons name="chevron-down" size={16} color="#8E8E93" />
        </TouchableOpacity>
      </View>

      <View style={styles.syncSetting}>
        <Text style={styles.syncSettingLabel}>Sync Interval (minutes)</Text>
        <TextInput
          style={styles.syncSettingInput}
          value={settings.syncInterval.toString()}
          onChangeText={(text) => onSettingsChange({ 
            ...settings, 
            syncInterval: parseInt(text) || 5 
          })}
          keyboardType="numeric"
        />
      </View>
    </View>
  );
};

// Sync Log Component
export const SyncLog = ({ logs, onClearLogs }) => {
  const getLogIcon = (type) => {
    switch (type) {
      case 'success': return 'checkmark-circle';
      case 'error': return 'close-circle';
      case 'warning': return 'warning';
      case 'info': return 'information-circle';
      default: return 'help-circle';
    }
  };

  const getLogColor = (type) => {
    switch (type) {
      case 'success': return '#10B981';
      case 'error': return '#EF4444';
      case 'warning': return '#F59E0B';
      case 'info': return '#667eea';
      default: return '#8E8E93';
    }
  };

  return (
    <View style={styles.syncLogContainer}>
      <View style={styles.syncLogHeader}>
        <Text style={styles.syncLogTitle}>Sync Log</Text>
        <TouchableOpacity style={styles.clearLogsButton} onPress={onClearLogs}>
          <Text style={styles.clearLogsText}>Clear</Text>
        </TouchableOpacity>
      </View>

      <ScrollView style={styles.syncLogList} showsVerticalScrollIndicator={false}>
        {logs.length === 0 ? (
          <View style={styles.emptyLogContainer}>
            <Ionicons name="document-text-outline" size={48} color="#E5E5EA" />
            <Text style={styles.emptyLogText}>No sync activities yet</Text>
          </View>
        ) : (
          logs.map((log) => (
            <View key={log.id} style={styles.syncLogItem}>
              <Ionicons 
                name={getLogIcon(log.type)} 
                size={20} 
                color={getLogColor(log.type)} 
              />
              <View style={styles.syncLogContent}>
                <Text style={styles.syncLogMessage}>{log.message}</Text>
                <Text style={styles.syncLogTime}>
                  {new Date(log.timestamp).toLocaleString()}
                </Text>
              </View>
            </View>
          ))
        )}
      </ScrollView>
    </View>
  );
};

// Main Sync System Component
export const WebsiteSyncSystem = ({ websiteId, companyId, onSyncComplete }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [syncSettings, setSyncSettings] = useState({
    autoSync: true,
    realTimeUpdates: true,
    conflictResolution: 'app',
    syncInterval: 5,
  });
  const [syncLogs, setSyncLogs] = useState([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showLogs, setShowLogs] = useState(false);

  const wsManager = useRef(new WebSocketManager());
  const apiManager = useRef(new APIManager('https://api.jobeng.ai'));
  const syncInterval = useRef(null);

  // Initialize sync system
  useEffect(() => {
    initializeSyncSystem();
    return () => {
      cleanup();
    };
  }, []);

  // Auto sync interval
  useEffect(() => {
    if (syncSettings.autoSync && isConnected) {
      startAutoSync();
    } else {
      stopAutoSync();
    }
    return () => stopAutoSync();
  }, [syncSettings.autoSync, syncSettings.syncInterval, isConnected]);

  const initializeSyncSystem = async () => {
    try {
      // Load saved settings
      const savedSettings = await AsyncStorage.getItem(`sync_settings_${companyId || 'default'}`);
      if (savedSettings) {
        setSyncSettings(JSON.parse(savedSettings));
      }

      // Load sync logs
      const savedLogs = await AsyncStorage.getItem(`sync_logs_${companyId || 'default'}`);
      if (savedLogs) {
        setSyncLogs(JSON.parse(savedLogs));
      }

      // Connect to WebSocket
      connectWebSocket();

      // Load auth token
      const authToken = await AsyncStorage.getItem('auth_token');
      if (authToken) {
        apiManager.current.setAuthToken(authToken);
      }
    } catch (error) {
      console.error('Initialize sync system error:', error);
      addSyncLog('error', 'Failed to initialize sync system');
    }
  };

  const connectWebSocket = () => {
    const wsUrl = `wss://api.jobeng.ai/ws?websiteId=${websiteId}&companyId=${companyId || ''}`;
    
    wsManager.current.connect(
      wsUrl,
      () => {
        setIsConnected(true);
        addSyncLog('success', 'Connected to sync server');
      },
      () => {
        setIsConnected(false);
        addSyncLog('warning', 'Disconnected from sync server');
      },
      (error) => {
        addSyncLog('error', `Connection error: ${error.message}`);
      }
    );

    // Add event listeners
    wsManager.current.addListener('content_updated', handleContentUpdate);
    wsManager.current.addListener('website_updated', handleWebsiteUpdate);
    wsManager.current.addListener('sync_conflict', handleSyncConflict);
  };

  const handleContentUpdate = (payload) => {
    addSyncLog('info', `Content updated: ${payload.contentType} - ${payload.title}`);
    if (onSyncComplete) {
      onSyncComplete('content_updated', payload);
    }
  };

  const handleWebsiteUpdate = (payload) => {
    addSyncLog('info', `Website updated: ${payload.websiteName}`);
    if (onSyncComplete) {
      onSyncComplete('website_updated', payload);
    }
  };

  const handleSyncConflict = (payload) => {
    addSyncLog('warning', `Sync conflict detected: ${payload.conflictType}`);
    // Handle conflict based on settings
    if (syncSettings.conflictResolution === 'manual') {
      showConflictResolutionDialog(payload);
    } else {
      resolveConflictAutomatically(payload);
    }
  };

  const showConflictResolutionDialog = (conflict) => {
    Alert.alert(
      'Sync Conflict',
      `There's a conflict with ${conflict.contentType}. How would you like to resolve it?`,
      [
        {
          text: 'Use App Version',
          onPress: () => resolveConflict(conflict, 'app'),
        },
        {
          text: 'Use Website Version',
          onPress: () => resolveConflict(conflict, 'website'),
        },
        {
          text: 'Cancel',
          style: 'cancel',
        },
      ]
    );
  };

  const resolveConflictAutomatically = (conflict) => {
    resolveConflict(conflict, syncSettings.conflictResolution);
  };

  const resolveConflict = async (conflict, resolution) => {
    try {
      const result = await apiManager.current.request('/api/sync/resolve-conflict', {
        method: 'POST',
        body: JSON.stringify({
          conflictId: conflict.id,
          resolution,
        }),
      });

      addSyncLog('success', `Conflict resolved using ${resolution} version`);
      if (onSyncComplete) {
        onSyncComplete('conflict_resolved', result);
      }
    } catch (error) {
      addSyncLog('error', `Failed to resolve conflict: ${error.message}`);
    }
  };

  const startAutoSync = () => {
    if (syncInterval.current) {
      clearInterval(syncInterval.current);
    }

    syncInterval.current = setInterval(() => {
      performSync();
    }, syncSettings.syncInterval * 60 * 1000);
  };

  const stopAutoSync = () => {
    if (syncInterval.current) {
      clearInterval(syncInterval.current);
      syncInterval.current = null;
    }
  };

  const performSync = async () => {
    if (isSyncing) return;

    setIsSyncing(true);
    addSyncLog('info', 'Starting sync...');

    try {
      // Get local data
      const localWebsiteData = await AsyncStorage.getItem(`company_websites_${companyId || 'default'}`);
      const localContentData = await AsyncStorage.getItem(`website_content_${companyId || 'default'}`);

      // Sync to server
      if (localWebsiteData) {
        await apiManager.current.syncToWebsite(websiteId, {
          websites: JSON.parse(localWebsiteData),
          content: localContentData ? JSON.parse(localContentData) : {},
        });
      }

      // Sync from server
      const serverData = await apiManager.current.syncFromWebsite(websiteId);
      
      // Update local data
      if (serverData.websites) {
        await AsyncStorage.setItem(`company_websites_${companyId || 'default'}`, JSON.stringify(serverData.websites));
      }
      if (serverData.content) {
        await AsyncStorage.setItem(`website_content_${companyId || 'default'}`, JSON.stringify(serverData.content));
      }

      setLastSync(new Date().toISOString());
      addSyncLog('success', 'Sync completed successfully');

      if (onSyncComplete) {
        onSyncComplete('sync_complete', serverData);
      }
    } catch (error) {
      console.error('Sync error:', error);
      addSyncLog('error', `Sync failed: ${error.message}`);
    } finally {
      setIsSyncing(false);
    }
  };

  const addSyncLog = (type, message) => {
    const newLog = {
      id: Date.now().toString(),
      type,
      message,
      timestamp: new Date().toISOString(),
    };

    setSyncLogs(prev => {
      const updated = [newLog, ...prev].slice(0, 100); // Keep last 100 logs
      AsyncStorage.setItem(`sync_logs_${companyId || 'default'}`, JSON.stringify(updated));
      return updated;
    });
  };

  const clearSyncLogs = () => {
    setSyncLogs([]);
    AsyncStorage.removeItem(`sync_logs_${companyId || 'default'}`);
  };

  const handleSettingsChange = (newSettings) => {
    setSyncSettings(newSettings);
    AsyncStorage.setItem(`sync_settings_${companyId || 'default'}`, JSON.stringify(newSettings));
  };

  const cleanup = () => {
    wsManager.current.disconnect();
    stopAutoSync();
  };

  return (
    <View style={styles.container}>
      {/* Sync Status */}
      <SyncStatus
        isConnected={isConnected}
        lastSync={lastSync}
        onReconnect={connectWebSocket}
      />

      {/* Sync Controls */}
      <View style={styles.syncControls}>
        <TouchableOpacity
          style={[styles.syncButton, isSyncing && styles.syncButtonDisabled]}
          onPress={performSync}
          disabled={isSyncing}
        >
          {isSyncing ? (
            <ActivityIndicator size={20} color="#FFFFFF" />
          ) : (
            <Ionicons name="sync" size={20} color="#FFFFFF" />
          )}
          <Text style={styles.syncButtonText}>
            <Text>{isSyncing ? 'Syncing...' : 'Sync Now'}</Text>
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.settingsButton}
          onPress={() => setShowSettings(true)}
        >
          <Ionicons name="settings" size={20} color="#667eea" />
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.logsButton}
          onPress={() => setShowLogs(true)}
        >
          <Ionicons name="list" size={20} color="#667eea" />
          {syncLogs.length > 0 && (
            <View style={styles.logsBadge}>
              <Text style={styles.logsBadgeText}>{syncLogs.length}</Text>
            </View>
          )}
        </TouchableOpacity>
      </View>

      {/* Settings Modal */}
      <Modal
        visible={showSettings}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowSettings(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <TouchableOpacity onPress={() => setShowSettings(false)}>
              <Text style={styles.modalCloseText}>Done</Text>
            </TouchableOpacity>
            <Text style={styles.modalTitle}>Sync Settings</Text>
            <View style={{ width: 50 }} />
          </View>
          <ScrollView style={styles.modalContent}>
            <SyncSettings
              settings={syncSettings}
              onSettingsChange={handleSettingsChange}
            />
          </ScrollView>
        </View>
      </Modal>

      {/* Logs Modal */}
      <Modal
        visible={showLogs}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowLogs(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <TouchableOpacity onPress={() => setShowLogs(false)}>
              <Text style={styles.modalCloseText}>Done</Text>
            </TouchableOpacity>
            <Text style={styles.modalTitle}>Sync Logs</Text>
            <View style={{ width: 50 }} />
          </View>
          <View style={styles.modalContent}>
            <SyncLog
              logs={syncLogs}
              onClearLogs={clearSyncLogs}
            />
          </View>
        </View>
      </Modal>
    
      {/* AI Assistant */}
      <TouchableOpacity 
        onPress={() => setShowAIChat(true)} 
        style={{ position:'absolute', bottom:20, right:20, width:56, height:56, borderRadius:28, backgroundColor:'#6366F1', alignItems:'center', justifyContent:'center', elevation:8, shadowColor:'#6366F1', shadowOffset:{width:0,height:4}, shadowOpacity:0.3, shadowRadius:8, zIndex:999 }}>
        <MaterialCommunityIcons name="robot" size={24} color="#fff" />
      </TouchableOpacity>
      <AIChatWidget screenName="WebsiteSyncSystem" visible={showAIChat} onClose={() => setShowAIChat(false)} title="Sync AI" extraContext={{}} />

    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginVertical: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },

  // Sync Status Styles
  syncStatusContainer: {
    marginBottom: 16,
  },
  syncStatusHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  syncStatusIndicator: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  syncStatusText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
    flex: 1,
  },
  reconnectButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F2F2F7',
    alignItems: 'center',
    justifyContent: 'center',
  },
  lastSyncText: {
    fontSize: 14,
    color: '#8E8E93',
    marginTop: 4,
    marginLeft: 20,
  },

  // Sync Controls Styles
  syncControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  syncButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#667eea',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    gap: 8,
  },
  syncButtonDisabled: {
    backgroundColor: '#8E8E93',
  },
  syncButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  settingsButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#F2F2F7',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logsButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#F2F2F7',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  logsBadge: {
    position: 'absolute',
    top: -2,
    right: -2,
    backgroundColor: '#EF4444',
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  logsBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FFFFFF',
  },

  // Sync Settings Styles
  syncSettingsContainer: {
    padding: 16,
  },
  syncSettingsTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1C1C1E',
    marginBottom: 20,
  },
  syncSetting: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA',
  },
  syncSettingLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
    flex: 1,
  },
  syncSettingSelect: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F2F2F7',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    gap: 8,
  },
  syncSettingSelectText: {
    fontSize: 14,
    color: '#1C1C1E',
  },
  syncSettingInput: {
    backgroundColor: '#F2F2F7',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    fontSize: 14,
    color: '#1C1C1E',
    minWidth: 60,
    textAlign: 'center',
  },

  // Sync Log Styles
  syncLogContainer: {
    flex: 1,
    padding: 16,
  },
  syncLogHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  syncLogTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1C1C1E',
  },
  clearLogsButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    backgroundColor: '#F2F2F7',
  },
  clearLogsText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#667eea',
  },
  syncLogList: {
    flex: 1,
  },
  emptyLogContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },
  emptyLogText: {
    fontSize: 16,
    color: '#8E8E93',
    marginTop: 12,
  },
  syncLogItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA',
    gap: 12,
  },
  syncLogContent: {
    flex: 1,
  },
  syncLogMessage: {
    fontSize: 14,
    color: '#1C1C1E',
    marginBottom: 4,
  },
  syncLogTime: {
    fontSize: 12,
    color: '#8E8E93',
  },

  // Modal Styles
  modalContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1C1C1E',
  },
  modalCloseText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#667eea',
  },
  modalContent: {
    flex: 1,
  },
});

export default WebsiteSyncSystem;